'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ProductCard } from '../product/product-card';
import { ProductCardSkeleton } from '../product/product-card/skeleton';
import { Button } from '../ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { fytofarmGridImageSizes } from '@eliadmin/core/src/utils/fytofarm-card-image-sizes';

export function ProductsList({ locale }: { locale: string }) {
  const { t } = useTranslations();
  const { me, isInitialized } = useCustomerStore();

  const productsQuery = useInfiniteQuery({
    queryKey: [CacheKey.PRODUCTS, locale, me?.authToken ?? null],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchProducts({
        locale,
        authToken: me?.authToken,
        query: {
          page: pageParam,
          results: 12,
          images: false,
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
    enabled: isInitialized,
  });

  const products = productsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];
  const isLoading = productsQuery.isLoading;

  return (
    <section
      className={cn(
        'custom-section',
        'gap-12 pb-8 pt-4 sm:pb-12 sm:pt-6 md:pb-16 md:pt-12 lg:pt-16 xl:pb-[72px]',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:gap-x-6',
        )}
      >
        {isLoading
          ? [...Array(8)].map((_, index) => <ProductCardSkeleton key={index} />)
          : products.map((product, index) => (
              <ProductCard product={product} key={product.slug} priority={index === 0} imageSizes={fytofarmGridImageSizes} />
            ))}
      </div>
      <div
        className={cn('flex w-full items-center justify-center', {
          hidden: !productsQuery.hasNextPage,
        })}
      >
        <Button
          onClick={() => productsQuery.fetchNextPage()}
          isLoading={productsQuery.isFetchingNextPage}
        >
          {t('text2301')}
        </Button>
      </div>
    </section>
  );
}
